// Account contact form modal — Accounts-Contacts-01.
//
// Renders a single create/edit modal for the Contacts tab. Required
// fields are first/last name + email (validated client-side; the
// backend repeats the check). Setting the primary checkbox is the
// only path to flipping ``is_primary`` from this modal — the backend
// demotes the previous primary in the same transaction.
//
// Note: this is for EXTERNAL employer contacts. There's no Invite
// button here — the Employer Portal flow is intentionally deferred.

import { useEffect, useState } from "react";

import { Button, Modal } from "@/design-system";

import type { AccountContact, AccountContactInput } from "./types";


export type AccountContactFormMode = "create" | "edit";


export interface AccountContactFormModalProps {
  open: boolean;
  mode: AccountContactFormMode;
  /** When mode === "edit", the row to hydrate. */
  contact?: AccountContact | null;
  /** Whether another contact currently holds the primary slot —
   * surfaces a hint that promoting this row will replace them. */
  hasExistingPrimary: boolean;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (values: AccountContactInput) => void;
}


type FormValues = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  mobile_phone: string;
  job_title: string;
  department: string;
  notes: string;
  is_primary: boolean;
};


function emptyValues(): FormValues {
  return {
    first_name: "", last_name: "", email: "",
    phone: "", mobile_phone: "", job_title: "",
    department: "", notes: "", is_primary: false,
  };
}


function fromContact(c: AccountContact | null | undefined): FormValues {
  if (!c) return emptyValues();
  return {
    first_name: c.first_name ?? "",
    last_name: c.last_name ?? "",
    email: c.email ?? "",
    phone: c.phone ?? "",
    mobile_phone: c.mobile_phone ?? "",
    job_title: c.job_title ?? "",
    department: c.department ?? "",
    notes: c.notes ?? "",
    is_primary: Boolean(c.is_primary),
  };
}


// Mirror the backend regex so callers see consistent validation.
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;


export function AccountContactFormModal({
  open, mode, contact, hasExistingPrimary,
  saving, error, onClose, onSubmit,
}: AccountContactFormModalProps) {
  const [values, setValues] = useState<FormValues>(() => fromContact(contact));
  const [localError, setLocalError] = useState<string | null>(null);

  // Re-hydrate on open / mode / contact change so the modal never
  // shows stale fields when re-opened for a different row.
  useEffect(() => {
    if (open) {
      setValues(fromContact(contact));
      setLocalError(null);
    }
  }, [open, contact, mode]);

  const setField = <K extends keyof FormValues>(
    key: K, value: FormValues[K],
  ) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleClose = () => {
    setLocalError(null);
    onClose();
  };

  const handleSubmit = () => {
    const first = values.first_name.trim();
    const last = values.last_name.trim();
    const email = values.email.trim();
    if (!first) { setLocalError("First name is required."); return; }
    if (!last) { setLocalError("Last name is required."); return; }
    if (!email) { setLocalError("Email is required."); return; }
    if (!EMAIL_RE.test(email)) {
      setLocalError("Enter a valid email address."); return;
    }
    setLocalError(null);
    const payload: AccountContactInput = {
      first_name: first,
      last_name: last,
      email,
      phone: values.phone.trim() || null,
      mobile_phone: values.mobile_phone.trim() || null,
      job_title: values.job_title.trim() || null,
      department: values.department.trim() || null,
      notes: values.notes.trim() || null,
      is_primary: values.is_primary,
    };
    onSubmit(payload);
  };

  const submitLabel = mode === "edit"
    ? (saving ? "Saving…" : "Save contact")
    : (saving ? "Adding…" : "Add contact");
  const title = mode === "edit" ? "Edit contact" : "Add contact";
  const hint = mode === "edit" ? null : (
    <p className="text-[11px] text-text-tertiary">
      Adds an external contact at this Account. We will not send
      them an invitation — Employer Portal is coming soon.
    </p>
  );

  // Show the demotion warning only when the user is promoting THIS
  // row. On edit, if this row is already primary the toggle being
  // ON shouldn't surface the warning.
  const promotingFromNonPrimary =
    values.is_primary
    && hasExistingPrimary
    && !(mode === "edit" && contact?.is_primary);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="tertiary"
            onClick={handleClose}
            disabled={saving}
            data-testid="account-contact-form-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            data-testid="account-contact-form-submit"
            onClick={handleSubmit}
            disabled={saving}
          >
            {submitLabel}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        {hint}
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="First name"
            required
            value={values.first_name}
            testId="account-contact-form-first-name"
            onChange={(v) => setField("first_name", v)}
          />
          <Field
            label="Last name"
            required
            value={values.last_name}
            testId="account-contact-form-last-name"
            onChange={(v) => setField("last_name", v)}
          />
        </div>

        <Field
          label="Email"
          required
          type="email"
          value={values.email}
          testId="account-contact-form-email"
          onChange={(v) => setField("email", v)}
        />

        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Phone"
            value={values.phone}
            testId="account-contact-form-phone"
            onChange={(v) => setField("phone", v)}
          />
          <Field
            label="Mobile"
            value={values.mobile_phone}
            testId="account-contact-form-mobile-phone"
            onChange={(v) => setField("mobile_phone", v)}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Job title"
            value={values.job_title}
            testId="account-contact-form-job-title"
            onChange={(v) => setField("job_title", v)}
          />
          <Field
            label="Department"
            value={values.department}
            testId="account-contact-form-department"
            onChange={(v) => setField("department", v)}
          />
        </div>

        <label className="flex flex-col gap-1 text-xs">
          <span className="font-medium text-text-secondary">Notes</span>
          <textarea
            data-testid="account-contact-form-notes"
            value={values.notes}
            onChange={(e) => setField("notes", e.target.value)}
            rows={3}
            className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary outline-none focus:border-teal/50 focus:ring-1 focus:ring-teal/30"
          />
        </label>

        <label className="flex flex-col gap-1 text-xs">
          <span className="inline-flex items-center gap-2 text-sm font-medium text-text-primary">
            <input
              type="checkbox"
              data-testid="account-contact-form-primary"
              checked={values.is_primary}
              onChange={(e) => setField("is_primary", e.target.checked)}
            />
            Is primary contact
          </span>
          {promotingFromNonPrimary && (
            <span
              data-testid="account-contact-form-primary-warning"
              className="text-text-tertiary"
            >
              This will replace the current primary contact.
            </span>
          )}
        </label>

        {(localError || error) && (
          <p
            role="alert"
            data-testid="account-contact-form-error"
            className="text-xs font-medium text-ember-ink"
          >
            {localError ?? error}
          </p>
        )}
      </div>
    </Modal>
  );
}


function Field({
  label, required, value, type = "text", testId, onChange,
}: {
  label: string;
  required?: boolean;
  value: string;
  type?: "text" | "email" | "tel";
  testId: string;
  onChange: (next: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="font-medium text-text-secondary">
        {label}
        {required && <span aria-hidden="true" className="ml-0.5 text-ember-ink">*</span>}
      </span>
      <input
        type={type}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-border-subtle bg-canvas/40 px-3 py-2 text-sm text-text-primary outline-none focus:border-teal/50 focus:ring-1 focus:ring-teal/30"
      />
    </label>
  );
}
